import { useState, FunctionComponent } from 'react';
import get from 'lodash/get';
import curry from 'lodash/curry';

import { defaultGoalProperties, GoalGraphContext, GoalGraphContextState } from './GoalGraphContext';

export const preventRangeAlignmentWith = curry(function preventRangeAlignmentWith(
  otherProperty: string,
  nextValue: number,
  stateUpdater: (nextValue: number, prevState: GoalGraphContextState) => GoalGraphContextState,
  previousState: GoalGraphContextState,
): GoalGraphContextState {
  const otherValue = get(previousState, otherProperty);
  if (otherValue === nextValue) {
    return previousState;
  }

  return stateUpdater(nextValue, previousState);
});

export function updateRangeMin(
  nextMin: number,
  previousState: GoalGraphContextState,
): GoalGraphContextState {
  return {
    ...previousState,
    distributionData: {
      ...previousState.distributionData,
      range: {
        ...previousState.distributionData.range,
        minPoint: nextMin,
      },
    },
  };
}

export function updateRangeMax(
  nextMax: number,
  previousState: GoalGraphContextState,
): GoalGraphContextState {
  return {
    ...previousState,
    distributionData: {
      ...previousState.distributionData,
      range: {
        ...previousState.distributionData.range,
        maxPoint: nextMax,
      },
    },
  };
}

export const GoalGraphContextProvider: FunctionComponent<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const [goalState, updateGoalState] = useState(defaultGoalProperties);

  const updateDistributionMin = (nextMin: number) => {
    updateGoalState(
      preventRangeAlignmentWith('distributionData.range.maxPoint', nextMin, updateRangeMin) as (
        prevState: GoalGraphContextState,
      ) => GoalGraphContextState,
    );
  };

  const updateDistributionMax = (nextMax: number) => {
    updateGoalState(
      preventRangeAlignmentWith('distributionData.range.minPoint', nextMax, updateRangeMax) as (
        prevState: GoalGraphContextState,
      ) => GoalGraphContextState,
    );
  };

  const goalContext: GoalGraphContextState = {
    ...goalState,
    updateDistributionMin,
    updateDistributionMax,
  };

  return <GoalGraphContext.Provider value={goalContext}>{children}</GoalGraphContext.Provider>;
};
